import { useState } from "react";
import { Loader2 } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { money } from "@/lib/rides";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Num } from "@/pages/admin/AdminPricing";

const Line = ({ label, value, testid }) => (
  <div className="flex justify-between gap-2 text-sm"><span className="text-muted-foreground">{label}</span><span className="shrink-0 font-semibold" data-testid={testid}>{value}</span></div>
);

function Result({ result }) {
  if (result.status === "pricing_review") return <p className="font-semibold text-amber-300" data-testid="sim-review">ADMIN QUOTE REQUIRED · {result.reason}</p>;
  const plan = result.recurring?.weeks ? result.recurring : null;
  return (
    <div className="space-y-1.5 border-t pt-3" data-testid="sim-result">
      {result.legs.map((l, i) => <Line key={i} label={`${l.from}→${l.to}: ${l.miles_charged} mi × $${l.rate.toFixed(2)}`} value={money(l.amount)} />)}
      <Line label={`Mileage ${money(result.mileage_raw)} rounded down`} value={money(result.mileage_subtotal)} testid="sim-mileage-subtotal" />
      <Line label={`Base fee (farthest ${result.farthest.miles} mi)`} value={`+${money(result.base_fee)}`} testid="sim-base-fee" />
      {result.waits.map((w, i) => <Line key={i} label={`${w.at} · ${w.minutes} min`} value={`+${money(w.fee)}`} />)}
      {result.airport.applied && <Line label="Airport" value={`+${money(result.airport.amount)}`} />}
      <Line label="Booking fee" value="$0.00" />
      <div className="flex justify-between border-t pt-2"><span className="font-semibold">Per ride</span><span className="font-display text-2xl font-bold neon-text-lime" data-testid="sim-total">{money(result.final_fare)}</span></div>
      {plan && (
        <Line label={`Week: ${plan.weeks[0].legs} legs${plan.weeks[0].qualifies ? ` · ${plan.pct}% off, rounded down to $${plan.round_down_to}` : " · no discount"}`}
          value={money(plan.weeks[0].total)} testid="sim-week-total" />
      )}
    </div>
  );
}

export function PricingSimulator({ rules }) {
  const [s, setS] = useState({ anchor_to_pickup: 12.8, trip: 15.9, destination_to_anchor: 13.4, farthest: "", waits: "", airport: false, rides: 0, legs: 1, payment: "end_of_week", vehicle: "xl" });
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const set = (k) => (v) => setS({ ...s, [k]: v });

  const run = async () => {
    setBusy(true);
    setError("");
    try {
      const { booking_fee, ...draft } = rules; // eslint-disable-line no-unused-vars
      const waits = String(s.waits).split(",").map((x) => x.trim()).filter(Boolean).map(Number);
      const { data } = await api.post("/admin/pricing/simulate", {
        rules: draft, vehicle_class: s.vehicle, anchor_to_pickup: Number(s.anchor_to_pickup), trip_legs: [Number(s.trip)],
        destination_to_anchor: Number(s.destination_to_anchor), farthest_miles: s.farthest === "" ? null : Number(s.farthest), waits,
        airport: s.airport, rides_per_week: Number(s.rides) || 0, legs_per_ride: Number(s.legs) || 1, payment: s.payment,
      });
      setResult(data);
    } catch (e) {
      setError(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="neon-border space-y-4 rounded-xl bg-card p-5" data-testid="pricing-simulator">
      <div>
        <h2 className="font-display text-lg font-semibold">Test a fare</h2>
        <p className="text-xs text-muted-foreground">Runs the real engine with the rules on this page (unsaved edits included). Use 0 for a leg that isn't driven.</p>
      </div>
      <div className="grid grid-cols-3 gap-2">
        <Num label="Anchor→Pickup mi" value={s.anchor_to_pickup} onChange={set("anchor_to_pickup")} testid="sim-anchor-miles" />
        <Num label="Trip mi" value={s.trip} onChange={set("trip")} testid="sim-trip-miles" />
        <Num label="Back to anchor mi" value={s.destination_to_anchor} onChange={set("destination_to_anchor")} testid="sim-return-miles" />
      </div>
      <div className="grid grid-cols-2 gap-2">
        <Num label="Farthest point mi (auto)" value={s.farthest} onChange={set("farthest")} testid="sim-farthest-miles" />
        <div className="space-y-1">
          <p className="text-xs text-muted-foreground">Waits (min, comma)</p>
          <input value={s.waits} onChange={(e) => set("waits")(e.target.value)} placeholder="10, 30" className="h-9 w-full rounded-md border bg-background px-2 text-sm" data-testid="sim-waits" />
        </div>
      </div>
      <div className="grid grid-cols-3 items-end gap-2">
        <Num label="Rides / week" step="1" value={s.rides} onChange={set("rides")} testid="sim-rides-per-week" />
        <Num label="Legs / ride" step="1" value={s.legs} onChange={set("legs")} testid="sim-legs-per-ride" />
        <select value={s.payment} onChange={(e) => set("payment")(e.target.value)} className="h-9 rounded-md border bg-background px-2 text-sm" data-testid="sim-payment">
          <option value="end_of_week">End of week</option><option value="prepaid">Prepaid</option>
        </select>
      </div>
      <div className="flex items-center gap-5 text-xs">
        <label className="flex items-center gap-2">Airport <Switch checked={s.airport} onCheckedChange={set("airport")} data-testid="sim-airport" /></label>
        <select value={s.vehicle} onChange={(e) => set("vehicle")(e.target.value)} className="h-8 rounded-md border bg-background px-2 text-sm" data-testid="sim-vehicle">
          <option value="xl">XL</option><option value="sedan">Sedan</option>
        </select>
      </div>
      <Button onClick={run} disabled={busy} className="w-full" data-testid="sim-run-button">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Calculate"}</Button>
      {error && <p className="text-sm text-destructive" data-testid="sim-error">{error}</p>}
      {result && <Result result={result} />}
    </section>
  );
}
